import {
  GstFreeResponseSchema,
  Gstr1aViewSchema,
  Gstr1SummaryResponseSchema,
  HsnSearchSchema,
  InvoiceListSchema,
  InvoiceResponseSchema,
  type ISSUE_ACTIONS,
  NextNoteNumberSchema,
  OriginalListSchema,
  type PutGstFree,
  ReturnDraftResponseSchema,
  type SaleInput,
  SavedBuyerListSchema,
} from '@oneapp/shared';
import { api } from '../lib/api';

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});
const enc = encodeURIComponent;
/** A sale's version, for If-Match: a change made elsewhere since it was opened isn't overwritten. */
const ifMatch = (version: number) => ({ 'If-Match': `"${version}"` });

export type SalesFilter = 'all' | 'b2b' | 'b2c' | 'notes';

/** GSTR-1 sales (Phase 6; see packages/shared/src/api.ts), as the signed-in person. */
export const salesApi = {
  draft: (returnId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/returns/${enc(returnId)}`, ReturnDraftResponseSchema, { auth: true, ...withSignal(signal) }),

  summary: (returnId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/returns/${enc(returnId)}/summary`, Gstr1SummaryResponseSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  invoices: (returnId: string, filter: SalesFilter, cursor: string | null, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (filter !== 'all') query.set('kind', filter);
    if (cursor) query.set('cursor', cursor);
    const qs = query.toString();
    return api.get(`/api/v1/returns/${enc(returnId)}/invoices${qs ? `?${qs}` : ''}`, InvoiceListSchema, {
      auth: true,
      ...withSignal(signal),
    });
  },

  invoice: (returnId: string, invoiceId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/returns/${enc(returnId)}/invoices/${enc(invoiceId)}`, InvoiceResponseSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  /** The same key for a retry of the same sale, so it's never added twice. */
  add: (returnId: string, sale: SaleInput, idempotencyKey: string) =>
    api.post(`/api/v1/returns/${enc(returnId)}/invoices`, InvoiceResponseSchema, {
      auth: true,
      body: sale,
      idempotencyKey,
    }),

  save: (returnId: string, invoiceId: string, sale: SaleInput, version: number) =>
    api.put(`/api/v1/returns/${enc(returnId)}/invoices/${enc(invoiceId)}`, InvoiceResponseSchema, {
      auth: true,
      body: sale,
      headers: ifMatch(version),
    }),

  remove: (returnId: string, invoiceId: string, version: number) =>
    api.delete(`/api/v1/returns/${enc(returnId)}/invoices/${enc(invoiceId)}`, ReturnDraftResponseSchema, {
      auth: true,
      headers: ifMatch(version),
    }),

  resolve: (returnId: string, issueId: string, action: (typeof ISSUE_ACTIONS)[number]) =>
    api.post(`/api/v1/returns/${enc(returnId)}/issues/${enc(issueId)}/resolve`, ReturnDraftResponseSchema, {
      auth: true,
      body: { action },
    }),

  hsn: (q: string, date: string, signal?: AbortSignal) =>
    api.get(`/api/v1/hsn?q=${enc(q)}&date=${enc(date)}&limit=6`, HsnSearchSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  buyers: (businessId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/businesses/${enc(businessId)}/buyers`, SavedBuyerListSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  /** Gap 4: invoices a credit or debit note can be for (by number or buyer, or one by id). */
  originals: (businessId: string, find: { q?: string; id?: string }, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (find.q) query.set('q', find.q);
    if (find.id) query.set('id', find.id);
    query.set('limit', '8');
    return api.get(`/api/v1/businesses/${enc(businessId)}/invoices?${query}`, OriginalListSchema, {
      auth: true,
      ...withSignal(signal),
    });
  },

  nextNoteNumber: (returnId: string, docType: 'credit' | 'debit', date: string, signal?: AbortSignal) =>
    api.get(
      `/api/v1/returns/${enc(returnId)}/next-note-number?docType=${docType}&date=${enc(date)}`,
      NextNoteNumberSchema,
      { auth: true, ...withSignal(signal) },
    ),

  gstFree: (returnId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/returns/${enc(returnId)}/gst-free`, GstFreeResponseSchema, { auth: true, ...withSignal(signal) }),

  putGstFree: (returnId: string, body: PutGstFree) =>
    api.put(`/api/v1/returns/${enc(returnId)}/gst-free`, GstFreeResponseSchema, { auth: true, body }),

  /** Gap 4b: a month's GSTR-1A, its fixes and what they do to GSTR-3B. */
  gstr1a: (returnId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/returns/${enc(returnId)}/gstr1a`, Gstr1aViewSchema, { auth: true, ...withSignal(signal) }),

  /** Gap 4b: a filed sale corrected in GSTR-1A; `version` is the correction's, once there is one. */
  correct: (returnId: string, invoiceId: string, sale: SaleInput, version: number | null) =>
    api.put(`/api/v1/returns/${enc(returnId)}/corrections/${enc(invoiceId)}`, InvoiceResponseSchema, {
      auth: true,
      body: sale,
      ...(version === null ? {} : { headers: ifMatch(version) }),
    }),
};

export const salesKeys = {
  all: (returnId: string) => ['sales', returnId] as const,
  draft: (returnId: string) => ['sales', returnId, 'draft'] as const,
  summary: (returnId: string) => ['sales', returnId, 'summary'] as const,
  invoices: (returnId: string, filter: SalesFilter) => ['sales', returnId, 'invoices', filter] as const,
  invoice: (returnId: string, invoiceId: string) => ['sales', returnId, 'invoice', invoiceId] as const,
  hsn: (q: string, date: string) => ['hsn', q, date] as const,
  buyers: (businessId: string) => ['buyers', businessId] as const,
  originals: (businessId: string, q: string, id: string) => ['originals', businessId, q, id] as const,
  nextNote: (returnId: string, docType: string, date: string) => ['next-note', returnId, docType, date] as const,
  gstFree: (returnId: string) => ['sales', returnId, 'gst-free'] as const,
  gstr1a: (returnId: string) => ['sales', returnId, 'gstr1a'] as const,
};

/** "/returns/{id}/sales", "/returns/{id}/sales/new" or "/returns/{id}/sales/{invoice}", as a view. */
export type SalesView =
  | { kind: 'list' }
  | { kind: 'new' }
  | { kind: 'edit'; invoiceId: string }
  /** Phase 16: walk-in sales by rate, on their own screen. */
  | { kind: 'walkIn' }
  /**
   * Gap 4: a credit or debit note, new (for the invoice `against`, when given) or to change (`invoiceId`):
   * "/returns/{id}/notes/new?against={invoice}&doc=debit", "/returns/{id}/notes/{note}".
   */
  | { kind: 'note'; invoiceId?: string; against?: string; docType?: 'credit' | 'debit' }
  /** Gap 4: the month's GST-free sales (Table 8): "/returns/{id}/gst-free". */
  | { kind: 'gstFree' }
  /**
   * Gap 4b, in a GSTR-1A: the filed sales to pick one to correct ("/returns/{id}/correct"), and one corrected
   * ("/returns/{id}/correct/{filed sale}").
   */
  | { kind: 'correct' }
  | { kind: 'correctOne'; invoiceId: string };

/** The note view a "/returns/{id}/notes/…" path (with its query) is. */
function noteView(rest: string, search: string): SalesView {
  const query = new URLSearchParams(search);
  const against = query.get('against');
  const doc = query.get('doc');
  return {
    kind: 'note',
    ...(rest !== 'new' ? { invoiceId: rest } : {}),
    ...(rest === 'new' && against && /^[\w-]{1,64}$/.test(against) ? { against } : {}),
    ...(doc === 'debit' || doc === 'credit' ? { docType: doc } : {}),
  };
}

export function parseSalesPath(full: string): { returnId: string; view: SalesView } | null {
  const [path = '', search = ''] = full.split('?');
  const note = /^\/returns\/([\w-]{1,64})\/notes\/([\w-]{1,64})\/?$/.exec(path);
  if (note) return { returnId: note[1] as string, view: noteView(note[2] as string, search) };
  const free = /^\/returns\/([\w-]{1,64})\/gst-free\/?$/.exec(path);
  if (free) return { returnId: free[1] as string, view: { kind: 'gstFree' } };
  const correct = /^\/returns\/([\w-]{1,64})\/correct(?:\/([\w-]{1,64}))?\/?$/.exec(path);
  if (correct) {
    const invoiceId = correct[2];
    return {
      returnId: correct[1] as string,
      view: invoiceId ? { kind: 'correctOne', invoiceId } : { kind: 'correct' },
    };
  }
  const m = /^\/returns\/([\w-]{1,64})\/sales(?:\/([\w-]{1,64}))?\/?$/.exec(path);
  if (!m) return null;
  const returnId = m[1] as string;
  const rest = m[2];
  if (!rest) return { returnId, view: { kind: 'list' } };
  if (rest === 'new') return { returnId, view: { kind: 'new' } };
  return { returnId, view: { kind: 'edit', invoiceId: rest } };
}
